import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studioProjects, studioBuilder, studioSecurity } from '@/lib/api';

const VOICE_BLOCKS = [
  { block_type: 'VoiceSoftphone', label: 'Softphone', desc: 'In-browser WebRTC calling widget' },
  { block_type: 'VoiceClickToCall', label: 'Click to Call', desc: 'Button that initiates an outbound call' },
  { block_type: 'VoiceCallWallboard', label: 'Call Center Wallboard', desc: 'Live queue health and agent presence' },
  { block_type: 'VoiceTranscription', label: 'Transcription Feed', desc: 'Real-time call transcript stream' },
] as const;

interface BlockConfig {
  block_type: string;
  block_id: string;
  label?: string;
  order: number;
  config: Record<string, unknown>;
  visible: boolean;
}

interface Project {
  id: string;
  name: string;
  status: string;
  blocks: BlockConfig[];
  branding: Record<string, unknown>;
}

interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function Builder() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [blocks, setBlocks] = useState<BlockConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [securityScore, setSecurityScore] = useState<number | null>(null);
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectId) return;
    async function load() {
      try {
        const [p, sec] = await Promise.allSettled([
          studioProjects.get(projectId!),
          studioSecurity.posture(projectId!),
        ]);
        if (p.status === 'fulfilled') {
          const proj = p.value as Project;
          setProject(proj);
          setBlocks(proj.blocks ?? []);
        }
        if (sec.status === 'fulfilled') {
          setSecurityScore((sec.value as { score: number }).score ?? null);
        }
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [projectId]);

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  async function saveBlocks() {
    if (!projectId) return;
    setSaving(true);
    try {
      await studioBuilder.updateBlocks(projectId, blocks);
    } finally {
      setSaving(false);
    }
  }

  function addVoiceBlock(blockType: string, label: string) {
    const newBlock: BlockConfig = {
      block_type: blockType,
      block_id: `${blockType}_${Date.now()}`,
      label,
      order: blocks.length,
      config: {},
      visible: true,
    };
    setBlocks((prev) => [...prev, newBlock]);
  }

  function removeBlock(blockId: string) {
    setBlocks((prev) => prev.filter((b) => b.block_id !== blockId));
  }

  async function sendAiMessage() {
    if (!aiInput.trim() || !projectId || aiLoading) return;
    const message = aiInput.trim();
    setAiInput('');
    setAiMessages((prev) => [...prev, { role: 'user', content: message }]);
    setAiLoading(true);
    try {
      const result = await studioBuilder.aiAssist(projectId, {
        mode: 'build',
        message,
        context: { current_blocks: blocks },
      });
      const reply = (result as { response?: string }).response ?? 'Done!';
      setAiMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } finally {
      setAiLoading(false);
    }
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading builder…</div>;
  if (!project) return <div className="p-8 text-muted-foreground">Project not found.</div>;

  return (
    <div className="flex h-screen">
      {/* Left: Block catalog */}
      <aside className="w-56 border-r border-border bg-card flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-border">
          <button onClick={() => navigate('/apps')} className="text-xs text-muted-foreground hover:text-foreground">
            ← My Apps
          </button>
          <h2 className="text-sm font-semibold text-foreground mt-1 truncate">{project.name}</h2>
          {securityScore !== null && (
            <p className={`text-xs mt-0.5 ${securityScore >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
              Security: {securityScore}/100
            </p>
          )}
        </div>
        <div className="p-3">
          <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider mb-2">Voice</p>
          {VOICE_BLOCKS.map((vb) => (
            <button
              key={vb.block_type}
              onClick={() => addVoiceBlock(vb.block_type, vb.label)}
              className="w-full text-left p-2 rounded-lg hover:bg-accent mb-1"
            >
              <p className="text-xs font-medium text-foreground">{vb.label}</p>
              <p className="text-[10px] text-muted-foreground">{vb.desc}</p>
            </button>
          ))}
        </div>
      </aside>

      {/* Center: Canvas */}
      <div className="flex-1 flex flex-col">
        <div className="border-b border-border p-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{blocks.length} block{blocks.length !== 1 ? 's' : ''}</span>
          <button
            onClick={() => void saveBlocks()}
            disabled={saving}
            className="bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {blocks.length === 0 ? (
            <div className="text-center text-muted-foreground mt-16">
              <p className="text-sm">Add blocks from the left panel or ask THEO.</p>
            </div>
          ) : (
            <div className="space-y-2 max-w-2xl mx-auto">
              {blocks.map((block) => (
                <div key={block.block_id} className="border border-border rounded-xl p-4 bg-card flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{block.label ?? block.block_type}</p>
                    <p className="text-xs text-muted-foreground">{block.block_type}</p>
                  </div>
                  <button
                    onClick={() => removeBlock(block.block_id)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: THEO */}
      <aside className="w-72 border-l border-border bg-card flex flex-col">
        <div className="p-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">THEO</h2>
          <p className="text-xs text-muted-foreground">Ask me to build, explain, or customize</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {aiMessages.length === 0 && (
            <p className="text-xs text-muted-foreground text-center mt-8">
              Try: "Add a ticket status widget" or "Explain what Softphone does"
            </p>
          )}
          {aiMessages.map((msg, i) => (
            <div
              key={i}
              className={`rounded-xl p-3 text-xs ${
                msg.role === 'user'
                  ? 'bg-primary/10 text-primary ml-4'
                  : 'bg-muted text-foreground mr-4'
              }`}
            >
              {msg.content}
            </div>
          ))}
          {aiLoading && (
            <div className="bg-muted rounded-xl p-3 text-xs text-muted-foreground mr-4 animate-pulse">
              Thinking…
            </div>
          )}
          <div ref={aiEndRef} />
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex gap-2">
            <input
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendAiMessage(); } }}
              placeholder="Ask the AI…"
              className="flex-1 text-xs border border-border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={() => void sendAiMessage()}
              disabled={!aiInput.trim() || aiLoading}
              className="bg-primary text-primary-foreground px-3 py-2 rounded-lg text-xs disabled:opacity-50"
            >
              →
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
